import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { APP_VERSION } from '../version';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VERSION_ENDPOINT = `${API_URL}/api/version`;
const RELOAD_COUNTDOWN_SEC = 5;
// Clé sessionStorage : nombre de reloads forcés par CETTE session de
// navigation. Limite à 1 pour empêcher toute boucle infinie de reload
// (cas où backend et frontend ont des versions différentes après build
// partiel ou env var APP_VERSION désynchronisé côté serveur).
const RELOAD_GUARD_KEY = 'autogere.versionReloadCount';
const MAX_RELOADS_PER_SESSION = 1;

/**
 * Compare deux versions sémantiques "X.Y.Z" (numériquement, en évitant
 * la comparaison de chaînes qui range "1.01.04" > "1.01.10").
 * Retourne > 0 si a > b, < 0 si a < b, 0 si égales.
 */
const compareVersions = (a, b) => {
  try {
    const pa = String(a || '0').split('.').map((x) => parseInt(x, 10) || 0);
    const pb = String(b || '0').split('.').map((x) => parseInt(x, 10) || 0);
    const len = Math.max(pa.length, pb.length);
    for (let i = 0; i < len; i += 1) {
      const da = pa[i] || 0;
      const db = pb[i] || 0;
      if (da !== db) return da - db;
    }
    return 0;
  } catch {
    return 0;
  }
};

/**
 * Vérifie si une nouvelle version est déployée. Ne déclenche un reload QUE si :
 *   1. La version du serveur est STRICTEMENT > version locale (pas seulement
 *      différente — un serveur en retard sur le frontend ne doit JAMAIS
 *      déclencher de reload).
 *   2. On n'a pas déjà rechargé ≥ MAX_RELOADS_PER_SESSION fois dans cette
 *      session navigateur (anti-boucle infinie).
 *
 * Retourne `{ server, updated }`.
 */
export const checkAppVersion = async ({ silentIfSame = true } = {}) => {
  try {
    const { data } = await axios.get(`${VERSION_ENDPOINT}?_=${Date.now()}`, {
      timeout: 8000,
      headers: { 'Cache-Control': 'no-cache' },
    });
    const serverVersion = data?.version;
    if (!serverVersion) {
      return { server: null, updated: false };
    }
    const cmp = compareVersions(serverVersion, APP_VERSION);
    if (cmp > 0) {
      // Garde anti-boucle : on n'autorise qu'un seul reload par session.
      const reloadCount = parseInt(sessionStorage.getItem(RELOAD_GUARD_KEY) || '0', 10);
      if (reloadCount >= MAX_RELOADS_PER_SESSION) {
        if (!silentIfSame) {
          toast.info(`Une mise à jour est disponible (v${serverVersion}) mais le rechargement a déjà été tenté. Fermez et rouvrez l'onglet.`);
        }
        return { server: serverVersion, updated: false };
      }
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(reloadCount + 1));
      toast.success(
        `Nouvelle version disponible (${serverVersion}). Rechargement automatique dans ${RELOAD_COUNTDOWN_SEC} s…`,
        { duration: RELOAD_COUNTDOWN_SEC * 1000 }
      );
      setTimeout(() => {
        try {
          window.location.reload(true);
        } catch {
          window.location.reload();
        }
      }, RELOAD_COUNTDOWN_SEC * 1000);
      return { server: serverVersion, updated: true };
    }
    if (cmp < 0) {
      // Serveur en RETARD sur le frontend — ne fait rien (situation transitoire
      // pendant un déploiement où le backend ne s'est pas encore restart).
      if (!silentIfSame) {
        toast.info(`Vous êtes à jour (v${APP_VERSION}). Le serveur est en cours de mise à jour.`);
      }
      return { server: serverVersion, updated: false };
    }
    // Versions identiques — déjà à jour.
    if (!silentIfSame) {
      toast.info(`Vous êtes déjà à jour (v${APP_VERSION}).`);
    }
    return { server: serverVersion, updated: false };
  } catch {
    if (!silentIfSame) {
      toast.error('Impossible de vérifier les mises à jour pour le moment.');
    }
    return { server: null, updated: false };
  }
};

/**
 * Hook : un SEUL check de version au montage de l'app (navigateur classique).
 *
 * **PWA spécifique** : en mode application installée, le composant App n'est
 * jamais unmounted quand l'utilisateur ferme la fenêtre — il est juste rendu
 * invisible. Donc le check au mount ne se redéclenche jamais à la réouverture.
 * On ajoute un check supplémentaire sur l'événement `visibilitychange` (au
 * retour de visibilité) uniquement en PWA, throttle à 1 check par minute max
 * pour ne pas marteler le backend.
 *
 * Pour vérifier manuellement, l'utilisateur clique « Mettre à jour » dans Paramètres.
 */
export const useVersionCheck = () => {
  const ranRef = useRef(false);
  const lastVisCheckRef = useRef(0);
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    checkAppVersion({ silentIfSame: true });

    // Détection PWA installée vs navigateur classique
    const isPWA = (() => {
      try {
        return window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
      } catch { return false; }
    })();
    if (!isPWA) return undefined;

    // En PWA : revérifier à chaque reprise de visibilité, throttle 60 s.
    const VIS_CHECK_THROTTLE_MS = 60 * 1000;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisCheckRef.current < VIS_CHECK_THROTTLE_MS) return;
      lastVisCheckRef.current = now;
      checkAppVersion({ silentIfSame: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
};

export default useVersionCheck;
